import {
	isEmptyString,
	isNull,
	isString,
	typeofArray,
	typeofObject,
	typeofString,
} from '../../../util/check';
import { getStringOfObjectOnKey } from '../../../util/object';
import { FnGridRow } from '../model/fn-grid-row';

/**
 * If value is undefined, null or blank, returns null, otherwise returns the value
 * @param {T} value
 * @returns {T | null}
 */
export function makeNull<T>(value?: T): T | null {
	return value == null || (value as any) === '' ? null : value;
}

export function exists<T>(value: T, allowEmptyString = false): boolean {
	return value != null && (allowEmptyString || (value as any) !== '');
}

export function missing<T>(value: T): boolean {
	return !exists(value);
}

export function missingOrEmpty<T>(value?: T[] | string): boolean {
	return !value || missing(value) || value.length === 0;
}

export function toStringOrNull(value: any): string | null {
	return exists(value) && value.toString ? value.toString() : null;
}

/** @deprecated */
export function referenceCompare<T>(left: T, right: T): boolean {
	if (left == null && right == null) {
		return true;
	}

	if (left == null && right != null) {
		return false;
	}

	if (left != null && right == null) {
		return false;
	}

	return left === right;
}

export function jsonEquals<T1, T2>(val1: T1, val2: T2): boolean {
	const val1Json = val1 ? JSON.stringify(val1) : null;
	const val2Json = val2 ? JSON.stringify(val2) : null;

	return val1Json === val2Json;
}

export function defaultComparator(
	valueA: any,
	valueB: any,
	accentedCompare: boolean = false
): number {
	const valueAMissing = valueA == null;
	const valueBMissing = valueB == null;

	// this is for aggregations sum and avg, where the result can be a number that is wrapped.
	// if we didn't do this, then the toString() value would be used, which would result in
	// the strings getting used instead of the numbers.
	if (valueA && valueA.toNumber) {
		valueA = valueA.toNumber();
	}

	if (valueB && valueB.toNumber) {
		valueB = valueB.toNumber();
	}

	if (valueAMissing && valueBMissing) {
		return 0;
	}

	if (valueAMissing) {
		return -1;
	}

	if (valueBMissing) {
		return 1;
	}

	function doQuickCompare<T>(a: T, b: T): number {
		return a > b ? 1 : a < b ? -1 : 0;
	}

	if (typeof valueA === 'string') {
		if (!accentedCompare) {
			return doQuickCompare(valueA, valueB);
		}

		try {
			// using local compare also allows chinese comparisons
			return valueA.localeCompare(valueB);
		} catch (e) {
			// if something wrong with localeCompare, eg not supported
			// by browser, then just continue with the quick one
			return doQuickCompare(valueA, valueB);
		}
	}

	return doQuickCompare(valueA, valueB);
}

export function isEmptyObject(_value: any):boolean {
	if (isNull(_value)) {
		return true;
	} else if (typeofArray(_value)) {
		return _value.length === 0;
	} else if (typeofString(_value)) {
		return isEmptyString(_value);
	} else if (typeofObject(_value)) {
		return isEmptyObject(_value);
	}
	return false;
}

function gridRowComprator(fields: Array<string>, isIgnoreCase?: boolean) {
	console.log('Field');
	console.log(fields);
	return (a: FnGridRow|any, b: FnGridRow|any) => {
		return fields
			.map((obj: any) => {
				let dir = 1;
				if (obj[0] === '-') {
					dir = -1;
					obj = obj.substring(1);
				}
				if (isIgnoreCase && isString(a.data[obj]) && isString(b.data[obj])) {
					a.data[obj] = a.data[obj].toLowerCase();
					b.data[obj] = b.data[obj].toLowerCase();
				}
				if (a.data[obj] > b.data[obj]) {
					return dir;
				}
				if (a.data[obj] < b.data[obj]) {
					return -dir;
				}
				return 0;
			})
			.reduce((p, n) => {
				return p ? p : n;
			}, 0);
	};
}

export function sortFnGridRow(
	objArray: Array<FnGridRow>,
	sortKeyStr: string,
	isIgnoreCase?: boolean
): Array<FnGridRow> {
	if (
		!isEmptyObject(sortKeyStr) &&
		!isEmptyObject(objArray) &&
		Array.isArray(objArray)
	) {
		// @ts-ignore
		return objArray.sort(gridRowComprator(sortKeyStr.split('~'), isIgnoreCase));
	} else {
		return objArray;
	}
}

export function find<T>(
	collection: T[] | { [id: string]: T },
	predicate: string | boolean | ((item: T) => boolean),
	value?: any
): T | null {
	if (collection === null || collection === undefined) {
		return null;
	}

	if (!Array.isArray(collection)) {
		const objToArray = values(collection);
		return find(objToArray, predicate, value);
	}

	const collectionAsArray = collection as T[];

	let firstMatchingItem: T | null = null;
	for (let i = 0; i < collectionAsArray.length; i++) {
		const item: T = collectionAsArray[i];

		if (typeof predicate === 'string') {
			if ((item as any)[predicate] === value) {
				firstMatchingItem = item;
				break;
			}
		} else {
			const callback = predicate as (item: T) => boolean;
			if (callback(item)) {
				firstMatchingItem = item;
				break;
			}
		}
	}

	return firstMatchingItem;
}

export function gridFilterArray<T>(
	inputArray: Array<T>,
	searchTerm: string,
	skipFilterKey?: boolean,
	filterKeyStr: string = ''
): Array<T> {
	const _filterArray = new Array<T>();
	const _serchTerm = searchTerm.toLocaleLowerCase();
	const _keyArray = filterKeyStr.split('~');
	for (let _i = 0; _i < inputArray.length; _i++) {
		const _fnRowItem:any = inputArray[_i];

		const _objectValStr = getStringOfObjectOnKey(
			_fnRowItem['data'] as Object,
			skipFilterKey,
			_keyArray
		);
		if (_objectValStr.toLocaleLowerCase().match(_serchTerm)) {
			_filterArray.push(_fnRowItem);
		}
	}
	return _filterArray;
}
export function filterNumColInRangeArray<T>(
	inputArray: Array<T>,
	numFrom: number,
	numTo: number,
	filterKeyStr: string = ''
): Array<T> {
	const _filterArray = new Array<T>();
	const _keyArray = filterKeyStr.split('~');
	for (let _i = 0; _i < inputArray.length; _i++) {
		let _fnRowItem:any = inputArray[_i];

		const _objectValStr = getStringOfObjectOnKey(
			_fnRowItem['data'] as Object,
			false,
			_keyArray
		);
		if (Number(_objectValStr) <= numTo && Number(_objectValStr) >= numFrom) {
			_filterArray.push(_fnRowItem);
		}
	}
	return _filterArray;
}

export function values<T>(
	object: { [key: string]: T } | Set<T> | Map<any, T>
): T[] {
	if (object instanceof Set || object instanceof Map) {
		const values: T[] = [];

		object.forEach((value: T) => values.push(value));

		return values;
	}

	return Object.keys(object).map((key) => object[key]);
}
